import { apiClient } from '../apiClient';

export type ApiJobDescriptionStatus = 'DRAFT' | 'ACTIVE' | 'CLOSE';
export type ApiJobPriority = 'HIGH' | 'MEDIUM' | 'LOW';

export type JobDescriptionListQuery = {
  page?: number;
  page_size?: number;
  search?: string;
  status?: ApiJobDescriptionStatus;
};

export type ApiJobDescriptionSummary = {
  id: string;
  title: string;
  department: string;
  /** Hiring unit (chatbot org chart ids) and recruited position: matched by CV reviewer rules. */
  department_id?: number | null;
  group_id?: number | null;
  position?: string | null;
  contract_type?: string | null;
  level?: string | null;
  location: string;
  priority: ApiJobPriority;
  status: ApiJobDescriptionStatus;
  ai_threshold: number;
  candidate_count?: number | null;
  created_at: string;
  updated_at: string;
};

export type CriteriaAttachmentRef = {
  original_file_name: string;
  blob_path: string;
};

export type BlobUploadTarget = {
  upload_url: string;
  method: string;
  required_headers: Record<string, string>;
  blob_container: string;
  blob_path: string;
  expires_at: string;
};

export type CriteriaUploadInitResponse = {
  original_file_name: string;
  upload: BlobUploadTarget;
};

export type ApiJobDescriptionDetail = ApiJobDescriptionSummary & {
  salary_min?: number | null;
  salary_max?: number | null;
  responsibilities: string;
  requirements: string;
  qualifications: string;
  bonus_points: string;
  contact_information: string;
  recruitment_information: string;
  criteria_attachment?: CriteriaAttachmentRef | null;
  pipeline_counts?: Record<string, number>;
};

export type ParsedJobDescriptionFields = {
  title: string | null;
  department: string | null;
  location: string | null;
  responsibilities: string | null;
  requirements: string | null;
  qualifications: string | null;
};

export type JobDescriptionDownloadUrl = {
  download_url: string;
  expires_at: string;
};

export type JobDescriptionListResponse = {
  items: ApiJobDescriptionSummary[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
  };
};

export type CreateJobDescriptionPayload = {
  title: string;
  department?: string | null;
  department_id?: number | null;
  group_id?: number | null;
  position?: string | null;
  contract_type?: string | null;
  level?: string | null;
  location?: string | null;
  priority?: ApiJobPriority;
  ai_threshold: number;
  salary_min?: number | null;
  salary_max?: number | null;
  responsibilities?: string | null;
  requirements: string;
  qualifications: string;
  bonus_points?: string | null;
  contact_information?: string | null;
  recruitment_information?: string | null;
  criteria_attachment?: CriteriaAttachmentRef | null;
  status: ApiJobDescriptionStatus;
};

export type UpdateJobDescriptionPayload = Partial<CreateJobDescriptionPayload>;

export type RescanResponse = {
  jd_id: string;
  accepted_application_count: number;
  status: 'QUEUED';
};

export type ApiOrgUnits = {
  departments: Array<{ id: number; name: string }>;
  groups: Array<{ id: number; name: string; department_id: number }>;
};

export const getOrgUnits = () => apiClient.get<ApiOrgUnits>('/org-units');

export const listJobDescriptions = (params?: JobDescriptionListQuery) =>
  apiClient.get<JobDescriptionListResponse>('/job-descriptions', params);

export const getJobDescription = (jdId: string) =>
  apiClient.get<ApiJobDescriptionDetail>(`/job-descriptions/${jdId}`);

export const postJobDescriptionDownloadUrl = (jdId: string) =>
  apiClient.post<JobDescriptionDownloadUrl>(`/job-descriptions/${jdId}/download-criteria`);

export const createJobDescription = (payload: CreateJobDescriptionPayload) =>
  apiClient.post<ApiJobDescriptionDetail>('/job-descriptions', payload);

export const updateJobDescription = (jdId: string, payload: UpdateJobDescriptionPayload) =>
  apiClient.patch<ApiJobDescriptionDetail>(`/job-descriptions/${jdId}`, payload);

export const createRescanJob = (jdId: string) =>
  apiClient.post<RescanResponse>(`/job-descriptions/${jdId}/rescans`);

const DOCX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const inferCriteriaContentType = (file: File): string => {
  const extension = file.name.split('.').pop()?.toLowerCase();

  if (extension === 'md' || extension === 'markdown') {
    return 'text/markdown';
  }

  if (extension === 'docx') {
    return DOCX_CONTENT_TYPE;
  }

  if (file.type === 'text/markdown' || file.type === 'text/plain') {
    return 'text/markdown';
  }

  if (file.type === DOCX_CONTENT_TYPE) {
    return DOCX_CONTENT_TYPE;
  }

  throw new Error('Only Markdown (.md) or Word (.docx) criteria files are supported');
};

export const parseJobDescriptionUpload = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  return apiClient.postForm<{ jd: ParsedJobDescriptionFields }>(
    '/job-descriptions/parse-upload',
    formData,
  );
};

export const uploadCriteriaAttachment = async (file: File): Promise<CriteriaAttachmentRef> => {
  const uploadInit = await apiClient.post<CriteriaUploadInitResponse>(
    '/job-descriptions/criteria-uploads',
    {
      original_file_name: file.name,
      content_type: inferCriteriaContentType(file),
      file_size: file.size,
      checksum_sha256: null,
    },
  );

  const response = await fetch(uploadInit.upload.upload_url, {
    method: uploadInit.upload.method || 'PUT',
    headers: uploadInit.upload.required_headers,
    body: file,
  });

  if (!response.ok) {
    throw new Error('Failed to upload criteria file to blob storage');
  }

  return {
    original_file_name: uploadInit.original_file_name,
    blob_path: uploadInit.upload.blob_path,
  };
};
